import { useMemo } from "react";

import isEmpty from "lodash/isEmpty";

import { useSchedulePermissionsContext } from "@/context-providers/SchedulePermissionsContext";
import usePatientStatus, {
  getUserTreatmentPhase,
} from "@/hooks/usePatientStatus";
import { useGetFullProfileQuery } from "@/store/api/user";
import { getIsSelectedUserInTreatmentForBooking } from "@/utils/scheduleBooking.util";

const useShouldShowBookAppointment = ({
  appUserId,
  isAppUserPatient,
  selectedUniversalStatusCode,
  selectedUserId,
}: {
  appUserId: string;
  isAppUserPatient: boolean;
  selectedUniversalStatusCode?: UniversalStatusCode;
  selectedUserId: string;
}): boolean | undefined => {
  const { data: fullProfileData } = useGetFullProfileQuery();
  const { isBookingPermissionsFetched, patientIdsWithBookingPermissions } =
    useSchedulePermissionsContext();
  const {
    hasLinkedPatientInTreatment,
    isCurrentUserInTreatment: isAppUserInTreatment,
  } = usePatientStatus(fullProfileData);
  const selectedUserPhase = getUserTreatmentPhase(selectedUniversalStatusCode);
  const isInTreatmentForBooking = getIsSelectedUserInTreatmentForBooking({
    appUserId,
    hasLinkedPatientInTreatment,
    isAppUserInTreatment,
    isAppUserPatient,
    selectedUserId,
    selectedUserPhase,
  });
  const shouldShowBookAppointment = useMemo(
    () =>
      !isEmpty(patientIdsWithBookingPermissions) &&
      isBookingPermissionsFetched &&
      isInTreatmentForBooking,
    [
      patientIdsWithBookingPermissions,
      isBookingPermissionsFetched,
      isInTreatmentForBooking,
    ],
  );
  return shouldShowBookAppointment;
};

export default useShouldShowBookAppointment;
